import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import {
  Dialog,
  DialogClose,
  DialogOverlay,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { TextAreaInput } from "../common/text-area-input";

/** 单行正文约 22px + py-2，最高 3 行 */
const TEXTAREA_MIN_PX = 38;
const TEXTAREA_MAX_PX = 82;

const autoGrowTextareaClass =
  "min-h-[38px] max-h-[82px] h-auto resize-none overflow-y-auto leading-[22px] text-sm py-2 px-3.5 rounded-sm w-full border border-[rgba(208,213,221,1)] text-[rgba(16,24,40,1)] focus:outline-none focus:border-black";

function fitPronunciationTextareaHeight(el: HTMLTextAreaElement | null) {
  if (!el) return;
  el.style.height = "auto";
  const h = Math.min(Math.max(el.scrollHeight, TEXTAREA_MIN_PX), TEXTAREA_MAX_PX);
  el.style.height = `${h}px`;
}

type ScriptPronunciationModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** 原文片段，只读展示 */
  originalText: string;
  /** 初始读音 */
  initialReading?: string;
  onRemove?: () => void;
  onSave: (reading: string) => void;
};

export function ScriptPronunciationModal({
  open,
  onOpenChange,
  originalText,
  initialReading = "",
  onRemove,
  onSave,
}: ScriptPronunciationModalProps) {
  const { t } = useTranslation();
  const [reading, setReading] = useState(initialReading);
  const originalRef = useRef<HTMLTextAreaElement>(null);
  const readingRef = useRef<HTMLTextAreaElement>(null);
  const justOpenedRef = useRef(false);

  useEffect(() => {
    if (open) {
      setReading(initialReading);
      justOpenedRef.current = true;
    }
  }, [open, initialReading]);

  useLayoutEffect(() => {
    if (!open) return;
    requestAnimationFrame(() => fitPronunciationTextareaHeight(originalRef.current));
  }, [open, originalText]);

  useLayoutEffect(() => {
    if (!open) return;
    requestAnimationFrame(() => {
      fitPronunciationTextareaHeight(readingRef.current);
      if (justOpenedRef.current) {
        const el = readingRef.current;
        if (el) {
          el.select();
          el.scrollTop = el.scrollHeight;
        }
        justOpenedRef.current = false;
      }
    });
  }, [open, reading]);

  const handleSave = () => {
    if (!reading.trim()) return;
    onSave(reading.trim());
    onOpenChange(false);
  };

  const showRemove = Boolean(initialReading.trim() && onRemove);

  const handleRemove = () => {
    onRemove?.();
    onOpenChange(false);
  };

  const handleSecondaryClick = () => {
    if (showRemove) {
      onRemove?.();
      onOpenChange(false);
      return;
    }
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogOverlay className="!bg-black/10" />
      <DialogContent
        showClose={false}
        className="sm:max-w-[400px] gap-5 p-5 rounded-xl border border-[#E4E7EC] shadow-lg"
      >
        <DialogHeader className="space-y-1">
          <DialogTitle className="flex items-center justify-between">
            <span className="text-base font-medium text-[#101828]">{t("agent_creation.input.pronunciation.modal_title")}</span>
            <button
              className="rounded-sm text-[#101828] opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none cursor-pointer"
              aria-label={t("common.close")}
              onClick={() => onOpenChange(false)}
            >
              <X className="size-5" strokeWidth={1.75} />
            </button>
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-3">
            <Label className="text-sm text-[rgba(102,112,133,1)]">
              {t("agent_creation.input.pronunciation.original")}
            </Label>
            <TextAreaInput
              ref={originalRef}
              readOnly
              tabIndex={-1}
              rows={1}
              value={originalText}
              aria-readonly="true"
              onChange={(e) => {
                setReading(e.target.value);
                requestAnimationFrame(() => fitPronunciationTextareaHeight(readingRef.current));
              }}
              className={cn(
                autoGrowTextareaClass,
                "bg-[#F9FAFB] cursor-default select-none text-[rgba(102,112,133,1)] hover:!border-[rgba(208,213,221,1)]"
              )}
            />
          </div>
          <div className="space-y-3">
            <Label className="text-sm text-[rgba(102,112,133,1)] flex items-center justify-between">
              {t("agent_creation.input.pronunciation.reading")}
              {showRemove && (
                <span className="text-sm text-[rgba(117,66,247,1)] hover:text-[#5f20ff] transition-colors cursor-pointer" onClick={handleRemove}>
                  {t("agent_creation.input.pronunciation.remove")}
                </span>
              )}
            </Label>
            <TextAreaInput
              ref={readingRef}
              value={reading}
              autoFocus
              rows={1}
              maxLength={100}
              placeholder={t("agent_creation.input.pronunciation.reading_placeholder")}
              className={cn(autoGrowTextareaClass, "border-[#E4E7EC]")}
              onChange={(e) => {
                setReading(e.target.value);
                requestAnimationFrame(() => fitPronunciationTextareaHeight(readingRef.current));
              }}
              onKeyDown={(e) => {
                if (e.nativeEvent.isComposing) return;
                if (e.key === "Escape") {
                  e.preventDefault();
                  onOpenChange(false);
                  return;
                }
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSave();
                }
              }}
            />
          </div>
        </div>
        <DialogFooter className="gap-2 sm:gap-2 sm:justify-end mt-2">
          <Button
            className="px-8"
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            {t("agent_creation.input.pronunciation.cancel")}
          </Button>
          <Button
            type="button"
            className="px-8 bg-[#101828] text-white hover:bg-[#101828]/90"
            disabled={!reading.trim()}
            onClick={handleSave}
          >
            {t("agent_creation.input.pronunciation.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
